import React from 'react';
import { motion } from 'framer-motion';
import {
    BeakerIcon,
    LightBulbIcon,
    GlobeAmericasIcon,
    ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';
import SEO from '../../components/SEO';

const Solutions = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    return (
        <div className="bg-gray-50 min-h-screen font-sans overflow-x-hidden">
            <SEO
                title="Agri Solutions - Smart Farming & Research Innovation"
                description="Discover our advanced agricultural solutions: Farmget App, Precision Farming, Hydroponics, and Post-Harvest Management."
                keywords="Agri Solutions, Smart Farming India, Hydroponics Solution, Precision Farming Technology, Agricultural Innovation"
            />
            {/* Hero Section */}
            <section className="relative h-[60vh] flex items-center justify-center bg-gray-900 text-white px-4">
                <div className="absolute inset-0 z-0">
                    <img
                        src="/assets/innovation_technology_farming.jpg"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80' }}
                        alt="Research"
                        className="w-full h-full object-cover opacity-40"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-gray-900 z-10"></div>
                </div>
                <div className="relative z-20 text-center max-w-4xl">
                    <motion.span
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-green-400 font-bold tracking-widest uppercase text-sm mb-4 block"
                    >
                        Future of Farming
                    </motion.span>
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6"
                    >
                        Research & <span className="text-green-400">Innovation</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-lg md:text-xl text-gray-300 font-light"
                    >
                        Pioneering sustainable agricultural technologies for a better tomorrow.
                    </motion.p>
                </div>
            </section>

            {/* Introduction */}
            <section className="py-16 px-4 md:py-24 md:px-6 bg-white">
                <div className="container mx-auto max-w-5xl text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">Science-Led Agriculture</h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-12">
                        Our dedicated R&D wing focuses on developing climate-resilient crops, bio-stimulants, and precision farming tools. We collaborate with leading agricultural universities and research institutes to bring lab innovations to the land.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                        {[
                            { title: "Bio-Inputs Development", desc: "Creating next-gen organic fertilizers and pest repellents.", icon: <BeakerIcon className="w-8 h-8" /> },
                            { title: "Smart Tools", desc: "IoT-based sensors for real-time soil and crop monitoring.", icon: <LightBulbIcon className="w-8 h-8" /> },
                            { title: "Climate Resilience", desc: "Developing protocols for farming in changing weather patterns.", icon: <GlobeAmericasIcon className="w-8 h-8" /> },
                        ].map((item, i) => (
                            <div key={i} className="p-6 bg-gray-50 rounded-2xl border border-gray-100">
                                <div className="text-green-600 mb-4">{item.icon}</div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                                <p className="text-gray-600 font-light">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Focus Areas */}
            <section className="py-16 px-4 md:py-24 md:px-6 bg-green-50">
                <div className="container mx-auto max-w-6xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                        <div>
                            <span className="text-green-600 font-bold tracking-widest uppercase text-sm mb-2 block">Our Impact</span>
                            <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">Field Trials & Results</h2>
                            <p className="text-gray-600 text-lg mb-6">
                                We don't just research; we validate. With over 500+ field trials conducted across different agro-climatic zones, our solutions are proven to deliver tangible results.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    "15% Increase in Average Yield",
                                    "30% Reduction in Water Usage",
                                    "Significant Improvement in Soil Carbon"
                                ].map((stat, i) => (
                                    <li key={i} className="flex items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
                                        <ArrowTrendingUpIcon className="w-6 h-6 text-green-500" />
                                        <span className="font-bold text-gray-800">{stat}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="relative h-[400px] rounded-3xl overflow-hidden shadow-2xl">
                            <img
                                src="https://images.unsplash.com/photo-1628352081506-83c43123ed6d?ixlib=rb-4.0.3&auto=format&fit=crop&w=1854&q=80"
                                alt="Field Trials"
                                className="object-cover w-full h-full"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Solutions;
